import * as Api from '@ViewModels';
import { RealMagicAccountIds } from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import { stringify as toQueryString } from 'query-string';
import * as React from 'react';
import { toast } from 'sonner';
import { isProdEnvironment } from '../../../../extViewmodels/sdk/Utils';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { LAUNCHDARKLY_SESSION_REPLAY_BASE_URL } from '../../../../models/SessionReplayLogger';
import {
	copyToClipboard,
	getSocialMediaPlatformLogoUrl,
	getValidAllowLevitateImpersonationUntil,
	isSupportedISP,
} from '../../../../models/UiUtils';
import { AccountUserViewModel, AdminPermission } from '../../../../viewmodels/AdminViewModels';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
} from '../../../../web/components-v2/Dropdown';
import { Avatar } from '../../../../web/components/Avatar';
import { DeleteUserModal } from '../../../../web/components/DeleteUserModal';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../../web/components/HoverCard';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { CancelIconFill } from '../../../../web/components/svgs/icons/CancelIconFill';
import { DangerIcon } from '../../../../web/components/svgs/icons/DangerIcon';
import { MainContactIcon } from '../../../../web/components/svgs/icons/MainContactIcon';
import { SuccessCheckIcon } from '../../../../web/components/svgs/icons/SuccessCheckIcon';
import { InfoIcon } from '../../../../web/components/svgs/icons/InfoIcon';
import { PopoverType, TinyPopover } from '../../../../web/components/TinyPopover';
import { brandPrimary, warningDark } from '../../../../web/styles/colors';
import { baseStyleSheet, bs } from '../../../../web/styles/styles';
import { ConditionalAccess } from '../../ConditionalAccess';
import { ManageInternalRolesModal } from '../ManageInternalRolesModal';
import { EmailConnectedUserMilestone, SocialConnectedUserMilestone, UserMilestone } from '../UserMilestone';
import { styleSheet } from './styles';

interface IProps extends IErrorMessageComponentProps {
	account: Api.IAccount;
	className?: string;
	grantTemporaryAccess?(): Promise<void>;
	importClick?(): void;
	isDialogShowing?: boolean;
	isLoadingUser?: boolean;
	onChangeEmail?(): void;
	onAuthenticatorChangeEmail?(): void;
	onSharedMailbox?(): void;
	onCreateWelcomeEmailClick?(): void;
	onExportCommunicationHistory?(): void;
	onGrantSlotMachineSpinClicked?(user: AccountUserViewModel): void;
	onImportHistory?(): void;
	onMakeMainContact?(): void;
	onUserDeleted?(): void;
	onUserErrorsClick?(): void;
	onUserNameClick?(): void;
	resendEmailClick?(): void;
	setUserForDeactivateConfirmDialog?(): void;
	styles?: StyleDeclarationValue[];
	updatingUser?: AccountUserViewModel;
	user: AccountUserViewModel;
	userClick?(): void;
}

function _AccountUserRow({ user, ...props }: IProps) {
	const errorMessages = useErrorMessages();
	const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);
	const [showManageRolesModal, setShowManageRolesModal] = React.useState(false);
	const activationStatus = user.activationStatus === 'INIT' ? 'CREATED' : user.activationStatus;
	const userRole = user.userRoleDescription;
	const color = user.userWithStats?.stats?.statusColor;
	const hasValidAllowLevitateImpersonate = getValidAllowLevitateImpersonationUntil(props.account);
	const userMileStoneClassName = css(styleSheet.userMilestone);
	const socialIsDisconnected = !!user?.socialMediaConnectedAccounts?.find(
		x => x.state && (x.state !== 'Connected' || !x.postTargetDisplayName)
	);

	let avatarStyle;
	switch (color) {
		case 'green':
			avatarStyle = styleSheet.avatarGreen;
			break;
		case 'red':
			avatarStyle = styleSheet.avatarRed;
			break;
		case 'yellow':
			avatarStyle = styleSheet.avatarYellow;
			break;
		case 'orange':
			avatarStyle = styleSheet.avatarOrange;
			break;
		case 'gray':
		default:
			break;
	}
	const handleNameClick = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		props.onUserNameClick?.();
	};
	const hasUserNameClick = !!props.onUserNameClick;
	const onCopyUserId = () => {
		const done = copyToClipboard(user.id);
		if (done) {
			toast.success('Copied user ID to clipboard!');
		} else {
			toast.error('Unable to copy to clipboard...');
		}
	};
	const openSessionReplays = () => {
		if (!user.id) return;
		const query = encodeURIComponent(`user=${user.id}`);
		const url = `${LAUNCHDARKLY_SESSION_REPLAY_BASE_URL}?query=${query}`;
		window.open(url);
	};
	const activateUser = () => {
		user
			.updateUserStatus('reactivate')
			?.then(result => {
				toast.success(`${getDisplayName(result)} set to ${result.activationStatus.toLocaleLowerCase()}.`);
			})
			.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
			});
	};
	const onRenderSocialTargetConnections = (_user: Api.IUser) => {
		return (
			<div className={css(styleSheet.targetContainer)}>
				{_user.socialMediaConnectedAccounts.map(x => {
					return (
						<span className={css(styleSheet.targetType)} key={x.type}>
							<img src={getSocialMediaPlatformLogoUrl(x.type)} />
							{x?.postTargetDisplayName || 'Unknown'}
							<span className={css(styleSheet.connectSuccess)}>
								{x.state === 'Connected' && !!x.postTargetDisplayName ? <SuccessCheckIcon /> : <CancelIconFill />}
							</span>
						</span>
					);
				})}
			</div>
		);
	};
	const onSendUserPasswordReset = () => {
		user
			.sendPasswordReset()
			?.then(() => {
				toast.success(`Password reset email has been sent to ${getDisplayName(user)}`);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
			});
	};
	const onDeleteUserClick = () => {
		setShowDeleteConfirmation(true);
	};

	const onDeleteConfirmationClose = (shouldDelete?: boolean) => {
		setShowDeleteConfirmation(false);
		if (shouldDelete) {
			const { onUserDeleted } = props;
			user
				.deleteAdmin()
				?.then(() => {
					toast.success('User successfully deleted');
					onUserDeleted?.();
				})
				.catch((err: Api.IOperationResultNoValue) => {
					errorMessages.pushApiError(err);
				});
		}
	};
	const onLoginAs = async () => {
		// In non-prod, auto-grant temporary access if not already granted
		if (!isProdEnvironment && !hasValidAllowLevitateImpersonate && props.grantTemporaryAccess) {
			try {
				await props.grantTemporaryAccess();
			} catch (err) {
				errorMessages.pushApiError(err as Api.IOperationResultNoValue);
				return;
			}
		}

		// Proceed with existing login logic
		user
			.getLoginAsCredentials()
			?.then(val => {
				const queryString = toQueryString(val.value);
				// Add impersonation tracking info for session replay
				const adminUser = user.userSession?.user;
				const impersonationParams = toQueryString({
					isImpersonated: 'true',
					impersonatedByUserId: adminUser?.id,
					impersonatedByUserName: getDisplayName(adminUser, false),
				});
				const location =
					props.account?.features?.aida?.enabled &&
					props.account?.features?.aida?.levitateUIStimulant === Api.LevitateUIStimulant.Decaf
						? `${import.meta.env.VITE_CUSTOMER_APP_AIDA_URL}#/customerSessionStart?${queryString}&${impersonationParams}`
						: `${import.meta.env.VITE_CUSTOMER_APP_URL}#/customerSessionStart?${queryString}&${impersonationParams}`;
				window.open(location, '_blank');
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
			});
	};
	const onGeneratePasswordResetLink = () => {
		// Create a new view model to avoid polluting the original with impersonation context
		const impersonatedUser = new AccountUserViewModel(user.userSession, user.toJs());
		impersonatedUser.impersonate({ account: props.account });

		impersonatedUser
			.generatePasswordResetLink()
			?.then(result => {
				const done = copyToClipboard(result.value);
				if (done) {
					toast.success('Password reset link copied to clipboard!');
				} else {
					toast.error('Unable to copy to clipboard...');
				}
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
			});
	};
	const onGrantSlotMachineSpinClicked = () => {
		const { onGrantSlotMachineSpinClicked: grantSpin } = props;
		grantSpin?.(user);
	};

	if (!!user.isBusy && !props.isDialogShowing) {
		return (
			<tr className={css(styleSheet.spinnerListItem)}>
				<td>
					<LoadingSpinner type='small' />
				</td>
			</tr>
		);
	}

	const prefs = user.preferences;
	const hasEmailSettings =
		!!prefs?.sendFromEmailAddress?.emailAddress ||
		!!prefs?.sendFromHtmlEmailAddress?.emailAddress ||
		!!prefs?.useSharedMailbox ||
		!!prefs?.hostedEmailAddress;

	const emailSettingsPopover = hasEmailSettings ? (
		<TinyPopover
			align='start'
			anchor={<InfoIcon fillColor={brandPrimary} />}
			contentStyles={[styleSheet.popoverStyle, styleSheet.emailSettingsPopover]}
			dismissOnOutsideAction={true}
			toggleOnHover={true}
			placement={['top']}
			type={PopoverType.white}
		>
			<div className={css(styleSheet.popoverContent)}>
				{prefs?.sendFromEmailAddress?.emailAddress && (
					<div>
						<strong>Sending As:</strong> {prefs.sendFromEmailAddress.emailAddress}
					</div>
				)}
				{prefs?.sendFromHtmlEmailAddress?.emailAddress && (
					<div>
						<strong>Send From HTML Email:</strong> {prefs.sendFromHtmlEmailAddress.emailAddress}
					</div>
				)}
				{prefs?.useSharedMailbox != null && (
					<div>
						<strong>Shared Mailbox:</strong>{' '}
						{prefs.sendFromEmailAddress?.emailAddress
							? `${prefs.sendFromEmailAddress.emailAddress} (${prefs.useSharedMailbox ? 'Enabled' : 'Disabled'})`
							: prefs.useSharedMailbox
								? 'Enabled'
								: 'Disabled'}
					</div>
				)}
				{prefs?.hostedEmailAddress && (
					<div>
						<strong>Hosted Email:</strong> {prefs.hostedEmailAddress}
					</div>
				)}
			</div>
		</TinyPopover>
	) : null;

	const unsupportedPopover = (
		<TinyPopover
			align='start'
			anchor={<DangerIcon backgroundColor={warningDark} borderColor={warningDark} />}
			contentStyles={[styleSheet.popoverStyle]}
			dismissOnOutsideAction={true}
			toggleOnHover={true}
			placement={['top']}
			type={PopoverType.white}
		>
			<div className={css(styleSheet.popoverContent)}>
				This user has an unsupported ISP email domain. We recommend the customer use an alternate email address to send
				group emails.
			</div>
		</TinyPopover>
	);

	return (
		<>
			{showManageRolesModal && (
				<ManageInternalRolesModal
					userId={user.id}
					userName={getDisplayName(user)}
					onClose={() => setShowManageRolesModal(false)}
				/>
			)}
			<DeleteUserModal isOpen={showDeleteConfirmation} onClose={onDeleteConfirmationClose} userName={user?.name} />
			<tr className={css(styleSheet.accountUserRow)}>
				<td className={css(styleSheet.avatarHeader)}>
					<Avatar
						user={user}
						className={css(styleSheet.avatar, avatarStyle)}
						icon={!isSupportedISP(user.primaryEmailDomain) ? unsupportedPopover : null}
					/>
				</td>
				<td className={`${css(styleSheet.employeeListName)} admin-accountDetail-employeeListItem-name`}>
					<div className={css(styleSheet.userCol)}>
						{hasUserNameClick ? (
							<a href='#' onClick={handleNameClick} className={css(styleSheet.userName)}>
								<div className={css(baseStyleSheet.truncateText)} id={user.id}>
									{user?.name || ''}{' '}
									{props.isLoadingUser ? <LoadingSpinner className={css(styleSheet.loading)} type='tiny' /> : null}
								</div>
							</a>
						) : (
							<div className={css(styleSheet.userName, baseStyleSheet.truncateText)} id={user.id}>
								{user?.name || ''}{' '}
								{props.isLoadingUser ? <LoadingSpinner className={css(styleSheet.loading)} type='tiny' /> : null}
							</div>
						)}
						<div className={css(styleSheet.userEmailRow)}>
							<div
								className={`${css(styleSheet.userEmail)} ${css(baseStyleSheet.truncateText)}`}
								title={user.toJs().primaryEmail.value}
							>
								{user.toJs().primaryEmail.value}
							</div>
							{emailSettingsPopover}
						</div>
						<div className={`${css(styleSheet.userEmail)} ${css(baseStyleSheet.truncateText)}`}>
							{userRole} ({activationStatus})
							{user.activationStatus === Api.ActivationStatus.INIT && (
								<span
									id={'resend-' + user.id}
									className={css(styleSheet.importedLinkedInLink)}
									onClick={props.resendEmailClick}
								>
									Send Welcome Email
								</span>
							)}
							{user.id === props.account.additionalInfo?.mainContactUserId && (
								<span className={css(styleSheet.mainContactIcon)}>
									{' | '}
									<MainContactIcon />
									Main Contact
								</span>
							)}
						</div>
						<div className={`${css(styleSheet.userEmail)} ${css(baseStyleSheet.truncateText)}`}>{user.id}</div>
					</div>
				</td>
				<td className={`${css(styleSheet.menuColumn)} `}>
					<ConditionalAccess permission={AdminPermission.ReadUser}>
						<Dropdown>
							<DropdownEllipsisTrigger />
							<DropdownContent styles={[bs.w60]}>
								<DropdownItem onClick={() => props.userClick?.()}>View Details/Stats</DropdownItem>
								{RealMagicAccountIds.has(props.account.id) && (
									<ConditionalAccess permission={AdminPermission.OnlyForDeveloperOps}>
										<DropdownItem onClick={() => setShowManageRolesModal(true)}>Manage Internal Roles</DropdownItem>
									</ConditionalAccess>
								)}
								<DropdownItem onClick={() => props.onImportHistory?.()}>View Import History/Stats</DropdownItem>
								<ConditionalAccess permission={AdminPermission.ModifyAccount}>
									<DropdownItem onClick={() => props.importClick?.()}>Import</DropdownItem>
								</ConditionalAccess>
								<ConditionalAccess permission={AdminPermission.ModifyUser}>
									{activationStatus !== 'CREATED' && (
										<DropdownItem onClick={() => openSessionReplays?.()}>View Session Replays</DropdownItem>
									)}
								</ConditionalAccess>
								<ConditionalAccess permission={AdminPermission.ModifyAccount} or={AdminPermission.OnlyForDevelopers}>
									{activationStatus === 'CREATED' && (
										<DropdownItem onClick={() => props.onCreateWelcomeEmailClick?.()}>
											Create Welcome Email Link
										</DropdownItem>
									)}
								</ConditionalAccess>
								<ConditionalAccess permission={AdminPermission.ViewUserErrors}>
									<DropdownItem onClick={() => props.onUserErrorsClick?.()}>View Recent Errors</DropdownItem>
								</ConditionalAccess>
								{user.activationStatus === Api.ActivationStatus.ACTIVE && (
									<>
										<ConditionalAccess permission={AdminPermission.ModifyAccount}>
											{props.account?.additionalInfo?.mainContactUserId !== user.id && (
												<DropdownItem onClick={() => props.onMakeMainContact?.()}>Make Main Contact</DropdownItem>
											)}
										</ConditionalAccess>
										<ConditionalAccess permission={AdminPermission.ModifyUser}>
											<DropdownItem onClick={() => props.setUserForDeactivateConfirmDialog?.()}>
												Deactivate User
											</DropdownItem>
										</ConditionalAccess>
									</>
								)}
								<ConditionalAccess permission={AdminPermission.ModifyUser}>
									{user.activationStatus === Api.ActivationStatus.DEACTIVATED && (
										<DropdownItem onClick={activateUser}>Reactivate User</DropdownItem>
									)}
								</ConditionalAccess>
								<ConditionalAccess permission={AdminPermission.ResetPassword}>
									<DropdownItem onClick={onSendUserPasswordReset}>Send Password Reset Email</DropdownItem>
								</ConditionalAccess>
								<ConditionalAccess permission={AdminPermission.SupportLevel3}>
									<DropdownItem onClick={onGeneratePasswordResetLink}>Generate Password Reset Link</DropdownItem>
								</ConditionalAccess>
								<ConditionalAccess permission={AdminPermission.ReadUser}>
									<DropdownItem onClick={onCopyUserId}>Copy User ID</DropdownItem>
								</ConditionalAccess>
								<ConditionalAccess permission={AdminPermission.SupportLevel3}>
									{user.activationStatus === Api.ActivationStatus.ACTIVE && (
										<DropdownItem onClick={() => props.onChangeEmail?.()}>Change User email</DropdownItem>
									)}
								</ConditionalAccess>
								<ConditionalAccess permission={AdminPermission.SupportLevel3}>
									{user.activationStatus === Api.ActivationStatus.ACTIVE &&
										user.enabledAuthenticators?.includes(Api.AuthenticatorType.Email) && (
											<DropdownItem onClick={() => props.onAuthenticatorChangeEmail?.()}>
												Change Two-Factor Authentication Email
											</DropdownItem>
										)}
								</ConditionalAccess>
								<ConditionalAccess permission={AdminPermission.ModifyUser}>
									{(user.emailProvider === 'Office365' ||
										user.emailProvider === 'Gmail' ||
										user.emailProvider === 'Google' ||
										// We want to display the option for o365 users who have not connected yet. Google and Gmail require a connection as we fetch the aliases.
										props.account.emailProviderConfigurations.some(ep => ep.emailProvider === 'Office365')) && (
										<DropdownItem onClick={() => props.onSharedMailbox?.()}>Shared Mailbox/Aliases</DropdownItem>
									)}
								</ConditionalAccess>
								{user.activationStatus === Api.ActivationStatus.INIT && (
									<ConditionalAccess permission={AdminPermission.ModifyUser}>
										<DropdownItem onClick={onDeleteUserClick}>Delete User</DropdownItem>
									</ConditionalAccess>
								)}
								{user.activationStatus === Api.ActivationStatus.ACTIVE &&
									(!isProdEnvironment || hasValidAllowLevitateImpersonate) && (
										<ConditionalAccess permission={AdminPermission.ModifyAccount}>
											<DropdownItem onClick={onLoginAs}>Login as</DropdownItem>
										</ConditionalAccess>
									)}
								{props.account.preferences.storeSubjectInCommunicationHistory &&
								user.activationStatus === Api.ActivationStatus.ACTIVE ? (
									<ConditionalAccess permission={AdminPermission.ModifyAccount}>
										<DropdownItem onClick={() => props.onExportCommunicationHistory?.()}>
											Export Recent Communication History
										</DropdownItem>
									</ConditionalAccess>
								) : null}
								{props.account.features?.aida?.enabled && user.activationStatus === Api.ActivationStatus.ACTIVE ? (
									<ConditionalAccess permission={AdminPermission.OnlyForDevelopers}>
										<DropdownItem onClick={onGrantSlotMachineSpinClicked}>Grant Slot Machine Spin</DropdownItem>
									</ConditionalAccess>
								) : null}
							</DropdownContent>
						</Dropdown>
					</ConditionalAccess>
				</td>
				<td className={userMileStoneClassName}>
					<EmailConnectedUserMilestone
						met={!!user?.emailCalendarTokenExists}
						disconnectedDate={user.reconnectRequired}
					/>
				</td>
				<td className={userMileStoneClassName}>
					<UserMilestone met={!!user?.userMilestones?.hasImportedContacts} />
				</td>
				<td className={userMileStoneClassName}>
					<UserMilestone met={!!user?.userMilestones?.hasSignatureTemplate} />
				</td>
				<td className={userMileStoneClassName}>
					<UserMilestone met={!!user?.userMilestones?.hasUsedEmailAddIn} />
				</td>
				<td className={userMileStoneClassName}>
					<UserMilestone met={!!user?.userMilestones?.hasUsedMobileApp} />
				</td>
				<td className={userMileStoneClassName}>
					<UserMilestone met={!!user?.userMilestones?.hasUsedTags} />
				</td>
				<td className={userMileStoneClassName}>
					<UserMilestone met={!!user?.userMilestones?.hasCreatedNote} />
				</td>
				<td className={userMileStoneClassName}>
					<UserMilestone met={!!user?.userMilestones?.hasCreatedNote} />
				</td>
				<td className={userMileStoneClassName}>
					<UserMilestone met={!!user?.userMilestones?.hasSentKeepInTouch} />
				</td>
				<td className={userMileStoneClassName}>
					<UserMilestone met={!!user?.toJs()?.lastTestEmail} />
				</td>
				{props.account.features.socialMedia?.enabled && (
					<td className={userMileStoneClassName}>
						{user?.socialMediaConnectedAccounts?.length ? (
							<HoverCard openDelay={100}>
								<HoverCardTrigger>
									<SocialConnectedUserMilestone
										met={user?.socialMediaConnectedAccounts?.length > 0}
										disconnected={socialIsDisconnected}
									/>
								</HoverCardTrigger>
								<HoverCardContent>{onRenderSocialTargetConnections(user)}</HoverCardContent>
							</HoverCard>
						) : (
							<SocialConnectedUserMilestone met={false} disconnected={socialIsDisconnected} />
						)}
					</td>
				)}
			</tr>
		</>
	);
}

const ObservableAccountRow = observer(_AccountUserRow);
export const AccountUserRow = inject(ErrorMessagesViewModelKey, UserSessionViewModelKey)(ObservableAccountRow);
